import { useEffect, useRef, useState } from "react";

import Icon from "@mui/material/Icon";
import { keyframes } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Khoảng kéo (px, đã tính hệ số "nặng tay" ở dưới) cần đạt để nhả tay là làm
// mới trang — cùng cỡ độ nhạy các app di động hay dùng (~70-80px).
const NGUONG_KICH_HOAT = 70;
// Kéo xa hơn mức này indicator không phình thêm nữa, tạo cảm giác "nặng tay"
// giống pull-to-refresh gốc thay vì kéo bao xa cũng phình bấy nhiêu.
const KEO_TOI_DA = 100;

const quay = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

function laDieuKhienForm(el) {
  return !!el?.closest?.("input, textarea, select, [contenteditable='true']");
}

// Kéo xuống từ đỉnh trang (chỉ khi đã cuộn lên hết) để làm mới trang — thay
// cho pull-to-refresh gốc của trình duyệt (Android Chrome có sẵn nhưng chỉ
// khi KHÔNG cài như app; iOS Safari và app cài vào màn hình chính đều không
// có gesture này) nên tự làm để chắc chắn chạy được trên mọi máy.
function KeoLamMoi() {
  const [dich, setDich] = useState(0);
  const [dangKeo, setDangKeo] = useState(false);
  const [dangLamMoi, setDangLamMoi] = useState(false);

  // Đọc/ghi trong touch handler qua ref để tránh đóng gói giá trị cũ (stale
  // closure) — effect chỉ mount 1 lần, không phụ thuộc state đang đổi liên
  // tục lúc kéo. State (dich/dangKeo/dangLamMoi) chỉ dùng để vẽ lại UI.
  const dichRef = useRef(0);
  const dangKeoRef = useRef(false);
  const dangLamMoiRef = useRef(false);
  const batDauYRef = useRef(null);

  useEffect(() => {
    function capNhatDich(v) {
      dichRef.current = v;
      setDich(v);
    }

    function xuLyBatDau(e) {
      if (dangLamMoiRef.current) return;
      if (laDieuKhienForm(e.target)) return;
      if ((document.scrollingElement?.scrollTop ?? 0) > 0) return;
      batDauYRef.current = e.touches[0].clientY;
      dangKeoRef.current = true;
      setDangKeo(true);
    }

    function xuLyDiChuyen(e) {
      if (!dangKeoRef.current || batDauYRef.current == null) return;
      const chenhLech = e.touches[0].clientY - batDauYRef.current;
      // Trang đã tự cuộn lên trong lúc kéo (vd nội dung tải thêm) hoặc kéo
      // ngược lên trên — huỷ gesture, không phải pull-to-refresh nữa.
      if (chenhLech <= 0 || (document.scrollingElement?.scrollTop ?? 0) > 0) {
        dangKeoRef.current = false;
        setDangKeo(false);
        capNhatDich(0);
        return;
      }
      capNhatDich(Math.min(KEO_TOI_DA, chenhLech * 0.5));
      // Chặn cuộn/bounce mặc định của trình duyệt trong lúc kéo để indicator
      // tự vẽ mượt theo đúng vị trí ngón tay, không giật với cuộn thật.
      e.preventDefault();
    }

    function xuLyKetThuc() {
      if (!dangKeoRef.current) return;
      dangKeoRef.current = false;
      setDangKeo(false);
      batDauYRef.current = null;
      if (dichRef.current >= NGUONG_KICH_HOAT) {
        dangLamMoiRef.current = true;
        setDangLamMoi(true);
        window.location.reload();
      } else {
        capNhatDich(0);
      }
    }

    window.addEventListener("touchstart", xuLyBatDau, { passive: true });
    window.addEventListener("touchmove", xuLyDiChuyen, { passive: false });
    window.addEventListener("touchend", xuLyKetThuc);
    window.addEventListener("touchcancel", xuLyKetThuc);
    return () => {
      window.removeEventListener("touchstart", xuLyBatDau);
      window.removeEventListener("touchmove", xuLyDiChuyen);
      window.removeEventListener("touchend", xuLyKetThuc);
      window.removeEventListener("touchcancel", xuLyKetThuc);
    };
  }, []);

  const dangHien = dich > 0 || dangLamMoi;
  const daSanSang = dich >= NGUONG_KICH_HOAT;

  return (
    <MDBox
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
        height: dangHien ? `${Math.max(dich, dangLamMoi ? 56 : 0)}px` : 0,
        overflow: "hidden",
        pointerEvents: "none",
        transition: dangKeo ? "none" : "height 200ms ease",
      }}
    >
      <MDBox
        sx={({ palette, boxShadows }) => ({
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          mb: 1,
          borderRadius: "999px",
          backgroundColor: palette.white.main,
          boxShadow: boxShadows.md,
        })}
      >
        <Icon
          sx={({ palette }) => ({
            color: palette.info.main,
            fontSize: "1.1rem !important",
            transform: dangLamMoi ? "none" : `rotate(${(dich / NGUONG_KICH_HOAT) * 180}deg)`,
            animation: dangLamMoi ? `${quay} 0.8s linear infinite` : "none",
          })}
        >
          {dangLamMoi ? "autorenew" : "arrow_downward"}
        </Icon>
        <MDTypography variant="caption" fontWeight="medium">
          {dangLamMoi
            ? "Đang làm mới..."
            : daSanSang
            ? "Thả ra để làm mới"
            : "Kéo xuống để làm mới"}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

export default KeoLamMoi;
